import React, { useEffect, useEffectEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Menu, MenuItem } from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import BusinessCenterIcon from '../../../assets/svg/menu/business-center.svg?react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { toast } from 'react-toastify';
import CalendarClockIcon from '../../../assets/svg/calendar-clock.svg?react';
import ContractEditIcon from '../../../assets/svg/menu/contract-edit.svg?react';
import { ROUTES } from '../constants/routes';
import { useGreetingUser } from '../../common/hooks/useWelcomingText';
import { useAuth, useOrganizationList, UserButton } from '@clerk/clerk-react';
import { GROUPS } from '../constants/groups';
import { RolesGate } from '../roles-gate';
import { AppHeaderProps } from './types';
import { useDocumensoRedirect } from '../hooks/useDocumensoRedirect';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';

const AppHeader = ({ title }: AppHeaderProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { orgId } = useAuth();
  const { setActive, userMemberships } = useOrganizationList({
    userMemberships: {
      pageSize: 1,
      keepPreviousData: true,
    },
  });
  const path = location.pathname;

  const { greeting, date } = useGreetingUser();

  const navigate = useNavigate();
  const { redirectToDocumenso } = useDocumensoRedirect();
  const [profilesAnchor, setProfilesAnchor] = useState<null | HTMLElement>(null);

  const activateOrganization = useEffectEvent((memOrgId: string) => {
    setActive?.({ organization: memOrgId });
  });

  useEffect(() => {
    const memOrgId = userMemberships?.data?.[0]?.organization.id;
    if (memOrgId) {
      if (orgId != memOrgId) {
        activateOrganization(memOrgId);
      }
    }
  }, [userMemberships, orgId]);
  return (
    <>
      <div className={'application-bar' + (path === ROUTES.HOME ? ' application-bar--home' : '')}>
        <Container
          maxWidth='lg'
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
          }}
        >
          {path === ROUTES.HOME ? (
            <div className='name-date-container'>
              <div className='name-container'>
                <h1>{greeting}</h1>
              </div>
              <div className='date-container'>{date}</div>
            </div>
          ) : title ? (
            <h1 className='application-bar-title' style={{ display: 'flex', alignItems: 'center' }}>
              {title}
            </h1>
          ) : null}
          <Box
            className='actions-container'
            sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}
          >
            <a
              id='appBarBookADemoLink'
              className='app-bar-link'
              aria-label={t('buttons.bookADemo')}
              href='https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1itJ_5Em1JU5o_x5yPX-ARg9ikUcJdxWvX2XCAVTshocDAoPHsmXrzqdwTvyyFVsFuo4pN9PEo'
              target='_blank'
              rel='noreferrer'
            >
              <CalendarClockIcon id='appBarBookADemoLinkCalendarMonthIcon' />
              <span className='app-bar-link-text'>{t('buttons.bookADemo')}</span>
            </a>
            {<UserButton />}
          </Box>
          {path === ROUTES.HOME && (
            <RolesGate group={GROUPS.COMPANY_ADMIN}>
              <Box className='home-actions'>
                <Link
                  id='appBarDocumentLibraryLink'
                  className='home-action home-action--primary'
                  aria-label={t('buttons.documentLibrary')}
                  to={ROUTES.DOCUMENT_LIBRARY + '?businessKitzLibrary=true'}
                >
                  <AccountBalanceOutlinedIcon
                    id='appBarDocumentLibraryLinkCollectionsBookmarkIcon'
                    fontSize='medium'
                  />
                  <span>{t('buttons.library')}</span>
                </Link>
                <Button
                  id='appBarCreateProfilesBtn'
                  className='home-action'
                  startIcon={<PersonAddAltOutlinedIcon />}
                  endIcon={<KeyboardArrowDownIcon />}
                  aria-haspopup='menu'
                  aria-expanded={Boolean(profilesAnchor)}
                  aria-controls={profilesAnchor ? 'appBarCreateProfilesMenu' : undefined}
                  onClick={(e) => setProfilesAnchor(e.currentTarget)}
                >
                  {t('buttons.createProfiles')}
                </Button>
                <Menu
                  id='appBarCreateProfilesMenu'
                  anchorEl={profilesAnchor}
                  open={Boolean(profilesAnchor)}
                  onClose={() => setProfilesAnchor(null)}
                  slotProps={{
                    paper: {
                      className: 'home-actions-menu',
                      style: { width: profilesAnchor?.offsetWidth },
                    },
                  }}
                >
                  <MenuItem
                    id='appBarCreateEmployeeMenuItem'
                    onClick={() => navigate(ROUTES.EMPLOYEE_PROFILE_CREATE)}
                  >
                    <BadgeOutlinedIcon
                      id='appBarCreateEmployeeMenuItemBadgeIcon'
                      fontSize='small'
                    />
                    {t('buttons.employee')}
                  </MenuItem>
                  <MenuItem
                    id='appBarCreateCustomerMenuItem'
                    onClick={() => navigate(ROUTES.CUSTOMER_PROFILE_CREATE)}
                  >
                    <SupportAgentOutlinedIcon
                      id='appBarCreateCustomerMenuItemSupportAgentIcon'
                      fontSize='small'
                    />
                    {t('buttons.customer')}
                  </MenuItem>
                  <MenuItem
                    id='appBarCreateSupplierMenuItem'
                    onClick={() => navigate(ROUTES.SUPPLIER_PROFILE_CREATE)}
                  >
                    <BusinessCenterIcon
                      id='appBarCreateSupplierMenuItemBusinessCenterIcon'
                      fontSize='small'
                    />
                    {t('buttons.supplier')}
                  </MenuItem>
                </Menu>
                <Button
                  id='appBarESignBtn'
                  className='home-action'
                  startIcon={<ContractEditIcon id='homePageSignBtnIcon' />}
                  onClick={() => {
                    redirectToDocumenso(ROUTES.DOCUMENTS).catch(() => {
                      toast.error(t('messages.failedDocumensoRedirect'));
                    });
                  }}
                >
                  {t('buttons.eSign')}
                </Button>
              </Box>
            </RolesGate>
          )}
        </Container>
      </div>
    </>
  );
};
export default AppHeader;
